import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Plane } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { z } from "zod";
import { LoadingSpinner } from "@/components/ui/loading-spinner";
import { lovable } from "@/integrations/lovable/index";
import { trackEvent } from "@/lib/productEvents";
import { usePlatform } from "@/hooks/usePlatform";
import { isNativeCapacitorApp } from "@/lib/runtime";

const authSchema = z.object({
  email: z.string().trim().email({ message: "Please enter a valid email address" }).max(255),
  password: z.string().min(6, { message: "Password must be at least 6 characters" }).max(72),
});

// Native Apple Sign-In uses the app bundle ID as the client identifier.
// The Supabase Apple provider must list this bundle ID under "Authorized Client IDs"
// for signInWithIdToken to validate the returned identity token.
const APPLE_NATIVE_CLIENT_ID = "com.airportbuddy.app";
// For the native flow the redirect URI is not actually navigated to — Apple just
// requires it to match a value configured for the bundle/Service ID. We use the
// production app domain (which is configured in the Apple Developer portal).
const APPLE_NATIVE_REDIRECT_URI = "https://airportbuddy.app/auth";

const toBase64Url = (bytes: Uint8Array) =>
  btoa(String.fromCharCode(...bytes))
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/=+$/, "");

const toHex = (bytes: Uint8Array) =>
  Array.from(bytes)
    .map((byte) => byte.toString(16).padStart(2, "0"))
    .join("");

const createNoncePair = async () => {
  const nonceBytes = new Uint8Array(32);
  crypto.getRandomValues(nonceBytes);

  const rawNonce = toBase64Url(nonceBytes);
  const hashedBuffer = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(rawNonce));
  // Native Apple Sign-In returns the nonce claim in the same format provided here.
  // The auth backend verifies Apple tokens by comparing a lowercase hex SHA-256 hash
  // of the raw nonce, so the Apple request must receive the hex hash, not base64url.
  const hashedNonce = toHex(new Uint8Array(hashedBuffer));

  return { rawNonce, hashedNonce };
};

const Auth = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { toast } = useToast();
  const { isNativeIOS } = usePlatform();
  const [isLogin, setIsLogin] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [emailError, setEmailError] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [googleLoading, setGoogleLoading] = useState(false);
  const [appleLoading, setAppleLoading] = useState(false);
  const requestedNextPath = searchParams.get("next");
  const defaultNextPath = isNativeIOS ? "/ios" : "/home";
  const nextPath = requestedNextPath?.startsWith("/")
    ? isNativeIOS && (requestedNextPath === "/home" || requestedNextPath === "/dashboard")
      ? "/ios"
      : requestedNextPath
    : defaultNextPath;

  useEffect(() => {
    // Check if user is already logged in
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session) {
        navigate(nextPath);
      }
    });
  }, [navigate, nextPath]);

  const validateField = (field: "email" | "password", value: string) => {
    if (field === "email") {
      const result = z.string().email().safeParse(value);
      setEmailError(result.success ? "" : "Please enter a valid email");
    } else {
      const result = z.string().min(6).safeParse(value);
      setPasswordError(result.success ? "" : "Password must be at least 6 characters");
    }
  };

  const isFormValid = email && password && !emailError && !passwordError && password.length >= 6;

  const handleAuth = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    // Validate input
    const validation = authSchema.safeParse({ email, password });
    if (!validation.success) {
      toast({
        variant: "destructive",
        title: "Validation Error",
        description: validation.error.issues[0].message,
      });
      setLoading(false);
      return;
    }

    try {
      if (isLogin) {
        const { error } = await supabase.auth.signInWithPassword({
          email: validation.data.email,
          password: validation.data.password,
        });

        if (error) {
          if (error.message.includes("Invalid login credentials")) {
            toast({
              variant: "destructive",
              title: "Login Failed",
              description: "The email or password you entered is incorrect. Please try again.",
            });
          } else {
            toast({
              variant: "destructive",
              title: "Error",
              description: error.message,
            });
          }
        } else {
          toast({
            title: "Welcome back!",
            description: "You've successfully logged in.",
          });
          navigate(nextPath);
        }
      } else {
        trackEvent("signup_start");
        const redirectUrl = `${window.location.origin}/`;
        const { error } = await supabase.auth.signUp({
          email: validation.data.email,
          password: validation.data.password,
          options: {
            emailRedirectTo: redirectUrl,
          },
        });

        if (error) {
          if (error.message.includes("already registered")) {
            toast({
              variant: "destructive",
              title: "Account Exists",
              description: "This email is already registered. Please sign in instead.",
            });
          } else {
            toast({
              variant: "destructive",
              title: "Error",
              description: error.message,
            });
          }
        } else {
          trackEvent("signup_complete");
          toast({
            title: "Welcome!",
            description: "Your account has been created successfully.",
          });
          navigate(nextPath);
        }
      }
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "An unexpected error occurred. Please try again.",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-secondary/20 to-primary/5 flex items-center justify-center p-4">
      <Card className="w-full max-w-md p-6 md:p-8 space-y-6 shadow-lg">
        <div className="text-center space-y-3">
          <div className="inline-flex items-center justify-center p-3 bg-gradient-to-br from-primary to-primary/80 rounded-2xl mb-2 shadow-md">
            <Plane className="h-8 w-8 text-primary-foreground" aria-hidden="true" />
          </div>
          <h1 className="text-2xl md:text-3xl font-bold text-foreground">
            {isLogin ? "Welcome Back" : "Create Account"}
          </h1>
          <p className="text-base text-muted-foreground">
            {isLogin
              ? "Sign in to access your airport dashboard"
              : "Sign up to start tracking your flights"}
          </p>
        </div>

        <form onSubmit={handleAuth} className="space-y-5">
          <div className="space-y-2">
            <Label htmlFor="email" className="text-base">Email</Label>
            <Input
              id="email"
              type="email"
              placeholder="you@example.com"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                if (e.target.value) validateField("email", e.target.value);
              }}
              onBlur={(e) => validateField("email", e.target.value)}
              required
              maxLength={255}
              disabled={loading}
              aria-describedby={emailError ? "email-error" : undefined}
              aria-invalid={!!emailError}
              className={emailError ? "border-destructive focus-visible:ring-destructive" : ""}
            />
            {emailError && (
              <p id="email-error" className="text-sm text-destructive" role="alert">
                {emailError}
              </p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="password" className="text-base">Password</Label>
            <Input
              id="password"
              type="password"
              placeholder="••••••••"
              value={password}
              onChange={(e) => {
                setPassword(e.target.value);
                if (e.target.value) validateField("password", e.target.value);
              }}
              onBlur={(e) => validateField("password", e.target.value)}
              required
              minLength={6}
              maxLength={72}
              disabled={loading}
              aria-describedby={passwordError ? "password-error" : "password-hint"}
              aria-invalid={!!passwordError}
              className={passwordError ? "border-destructive focus-visible:ring-destructive" : ""}
            />
            {passwordError ? (
              <p id="password-error" className="text-sm text-destructive" role="alert">
                {passwordError}
              </p>
            ) : (
              <p id="password-hint" className="text-sm text-muted-foreground">
                Must be at least 6 characters
              </p>
            )}
          </div>

          <Button
            type="submit"
            className="w-full bg-gradient-to-r from-primary to-primary/90 hover:from-primary/90 hover:to-primary/80 min-h-[48px]"
            disabled={loading || !isFormValid}
            size="lg"
          >
            {loading ? (
              <>
                <LoadingSpinner size="sm" className="mr-2" />
                {isLogin ? "Signing in..." : "Creating account..."}
              </>
            ) : (
              isLogin ? "Sign In" : "Sign Up"
            )}
          </Button>
        </form>

        <div className="relative">
          <div className="absolute inset-0 flex items-center">
            <span className="w-full border-t border-border" />
          </div>
          <div className="relative flex justify-center text-xs uppercase">
            <span className="bg-card px-2 text-muted-foreground">Or continue with</span>
          </div>
        </div>

        {!isNativeIOS && (
          <Button
            type="button"
            variant="outline"
            className="w-full min-h-[48px] text-base gap-3"
            disabled={loading || googleLoading || appleLoading}
            onClick={async () => {
              setGoogleLoading(true);
              const result = await lovable.auth.signInWithOAuth("google", {
                redirect_uri: window.location.origin,
              });
              if (result.error) {
                toast({
                  variant: "destructive",
                  title: "Google Sign-In Failed",
                  description: result.error.message || "Could not sign in with Google. Please try again.",
                });
                setGoogleLoading(false);
              } else if (!result.redirected) {
                navigate(nextPath);
              }
            }}
          >
            {googleLoading ? (
              <LoadingSpinner size="sm" />
            ) : (
              <svg className="h-5 w-5" viewBox="0 0 24 24">
                <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4" />
                <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
                <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
                <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
              </svg>
            )}
            Continue with Google
          </Button>
        )}

        <Button
          type="button"
          variant="outline"
          className="w-full min-h-[48px] text-base gap-3"
          disabled={loading || googleLoading || appleLoading}
          onClick={async () => {
            setAppleLoading(true);
            try {
              if (isNativeCapacitorApp()) {
                // Dynamically import the native plugin so the web build never tries to load it
                const { SignInWithApple } = await import("@capacitor-community/apple-sign-in");

                const { rawNonce, hashedNonce } = await createNoncePair();

                const result = await SignInWithApple.authorize({
                  clientId: APPLE_NATIVE_CLIENT_ID,
                  redirectURI: APPLE_NATIVE_REDIRECT_URI,
                  scopes: "email name",
                  nonce: hashedNonce,
                });

                const identityToken = result?.response?.identityToken;
                if (!identityToken) {
                  throw new Error("Apple did not return an identity token. Please try again.");
                }

                const { error } = await supabase.auth.signInWithIdToken({
                  provider: "apple",
                  token: identityToken,
                  nonce: rawNonce,
                });

                if (error) {
                  console.error("[Auth] Supabase signInWithIdToken (apple) error", error);
                  throw new Error(error.message || "Could not complete Apple Sign-In.");
                }
                navigate(nextPath);
              } else {
                const result = await lovable.auth.signInWithOAuth("apple", {
                  redirect_uri: window.location.origin,
                });
                if (result.error) throw result.error;
                if (!result.redirected) navigate(nextPath);
              }
            } catch (err: unknown) {
              // Apple error code 1001 = user canceled — silent
              const errorLike = err instanceof Error ? err : undefined;
              const code = typeof err === "object" && err !== null && "code" in err
                ? (err as { code?: string | number }).code
                : typeof err === "object" && err !== null && "error" in err
                  ? (err as { error?: string | number }).error
                  : undefined;
              const message = errorLike?.message || (typeof err === "object" && err !== null && "message" in err
                ? String((err as { message?: unknown }).message || "")
                : "");
              const isUserCancel =
                code === "1001" ||
                code === "ERR_CANCELED" ||
                message.includes("error 1001") ||
                message.toLowerCase().includes("canceled") ||
                message.toLowerCase().includes("cancelled");

              if (!isUserCancel) {
                console.error("[Auth] Apple Sign-In failed", { code, message, err });
                toast({
                  variant: "destructive",
                  title: "Apple Sign-In Failed",
                  description: message || "Could not sign in with Apple. Please try again.",
                });
              }
            } finally {
              setAppleLoading(false);
            }
          }}
        >
          {appleLoading ? (
            <LoadingSpinner size="sm" />
          ) : (
            <svg className="h-5 w-5" viewBox="0 0 24 24" fill="currentColor">
              <path d="M17.05 20.28c-.98.95-2.05.88-3.08.4-1.09-.5-2.08-.48-3.24 0-1.44.62-2.2.44-3.06-.4C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z" />
            </svg>
          )}
          Continue with Apple
        </Button>

        <div className="text-center">
          <button
            type="button"
            onClick={() => {
              setIsLogin(!isLogin);
              setEmailError("");
              setPasswordError("");
            }}
            className="text-base text-muted-foreground hover:text-foreground transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 rounded px-2 py-1"
            disabled={loading}
          >
            {isLogin
              ? "Don't have an account? Sign up"
              : "Already have an account? Sign in"}
          </button>
        </div>
      </Card>
    </div>
  );
};

export default Auth;
